/*
 * No.9 Caffè — Announcement bar marquee
 * Seamless, constant-speed horizontal marquee.
 *
 * Technique: take the authored group of messages, repeat its items until the
 * group is at least as wide as the viewport, then clone that full-width group
 * once. The track is now two identical halves, so animating translateX(-50%)
 * lands on a pixel-identical frame — the loop is seamless with no reset.
 * Speed is px/second, so it stays constant regardless of message length.
 */
class No9Marquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.no9-marquee__track');
    if (!this.track) return;

    this.speed = Math.max(1, parseFloat(this.dataset.speed) || 50); // px per second
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Pristine single-group markup, so every re-render starts clean.
    this.baseHTML = this.track.innerHTML;

    if (this.reduce) {
      this.classList.add('no9-marquee--static');
      return;
    }

    this.render();

    this._ro = new ResizeObserver(() => this.scheduleRender());
    this._ro.observe(this);

    // N27/Inter can change text width after first paint.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.scheduleRender());
    }
  }

  disconnectedCallback() {
    if (this._ro) this._ro.disconnect();
    clearTimeout(this._t);
  }

  scheduleRender() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.render(), 150);
  }

  render() {
    if (!this.track || this.reduce) return;

    this.classList.remove('no9-marquee--ready');
    this.track.innerHTML = this.baseHTML; // reset to a single group

    const base = this.track.querySelector('.no9-marquee__group');
    if (!base) return;

    const containerWidth = this.clientWidth;
    if (!containerWidth) return; // hidden / not laid out yet

    // Repeat the messages until one group spans the viewport (no gap on the loop).
    const itemsHTML = base.innerHTML;
    let guard = 0;
    while (base.scrollWidth < containerWidth && guard < 60) {
      base.insertAdjacentHTML('beforeend', itemsHTML);
      guard += 1;
    }

    const groupWidth = base.getBoundingClientRect().width;
    if (!groupWidth) return;

    // Duplicate the full-width group -> two identical halves -> seamless -50%.
    const dup = base.cloneNode(true);
    dup.setAttribute('aria-hidden', 'true');
    this.track.appendChild(dup);

    this.track.style.setProperty('--no9-marquee-duration', `${groupWidth / this.speed}s`);
    this.classList.add('no9-marquee--ready');
  }
}

if (!customElements.get('no9-marquee')) {
  customElements.define('no9-marquee', No9Marquee);
}
